<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import AddService from './AddService.vue';
import ServiceGoals from './ServiceGoals.vue';
import { can, useDateTime } from '@/composables';
import {
  TreatmentPlanServiceCreationInterface,
  TreatmentPlanServiceStudentInterface,
  TreatmentPlanServiceUpdateFormInterface,
  TreatmentPlanServiceUpdateInterface,
} from '@/types';
import { PersonStores } from '@/store';
import UpdateService from './UpdateService.vue';
import ConfirmAction from '@/components/ConfirmAction';
import ExpireSwitch from '@/components/ExpireTreatmentServiceSwitch/ExpireSwitch.vue';
import { PERMISSIONS } from '@/enum';

const loading = ref(true);
const services = ref<TreatmentPlanServiceStudentInterface[]>([]);
const toast = useToast();
const serviceModalOpen = ref(false);
const updateServiceModalOpen = ref(false);
const toggleExpireService = ref(false);
const toggleNoOrganizationModal = ref(false);
const actualService = ref<TreatmentPlanServiceUpdateInterface | number>();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const connectionStore = PersonStores.useUserConnectionsStore();
const loadingServiceModal = ref(false);
const expireInformation = ref(0);

onBeforeMount(() => connectionStore.getConnections());

onMounted(() => getServices());

const expandedRows = ref([]);

const getServices: () => Promise<void> = async () => {
  if (can([PERMISSIONS.TREATMENT_PLANS_SERVICES_INDEX])) {
    const filterExpired = treatmentPlansStore.showExpired ? { expired: '1' } : undefined;
    await treatmentPlansStore.getUserTreatmentPlanServices(filterExpired);
    services.value = treatmentPlansStore.treatmentPlanServices;
    loading.value = false;
  }
};

const openServicesModal = () => {
  loadingServiceModal.value = true;
  if (!connectionStore.$state.connections.organizations?.length) {
    toggleNoOrganizationModal.value = true;
  } else {
    serviceModalOpen.value = true;
    loadingServiceModal.value = false;
  }
};

const openUpdateServicesModal = (item: TreatmentPlanServiceUpdateInterface) => {
  actualService.value = item;
  updateServiceModalOpen.value = true;
};

const openExpireServiceModal = async (item: TreatmentPlanServiceUpdateInterface) => {
  actualService.value = item;
  expireInformation.value = (await treatmentPlansStore.getTreatmentPlanExpireInformation(item.id)).future_events;
  toggleExpireService.value = true;
};

const onAddService = async (newService: TreatmentPlanServiceCreationInterface) => {
  const response = await treatmentPlansStore.addNewTreatmentPlanServiceForPerson(newService);

  if (response.status === 201) {
    serviceModalOpen.value = false;
    await onSuccess('Service has been added', 'Saved');
    return;
  }

  onError(response?.data.message);
};

const onCancelAdd = () => (serviceModalOpen.value = false);

const onCancelUpdate = () => (updateServiceModalOpen.value = false);

const onSuccess = async (detail: string, summary: string) => {
  await getServices();
  toast.add({
    severity: 'success',
    summary: summary,
    detail: detail,
    life: 3000,
  });
};

const onError = (detail: string, severity?: string) => {
  toast.add({
    severity: severity ?? 'error',
    summary: 'Error',
    detail: detail,
    life: 3000,
  });
};

const onUpdateService = async (updatedService: TreatmentPlanServiceUpdateFormInterface) => {
  const response = await treatmentPlansStore.updateTreatmentPlanServiceForPerson(
    { ...updatedService },
    updatedService.id,
  );

  if (response.status === 200) {
    updateServiceModalOpen.value = false;
    await onSuccess('Service has been saved', 'Saved');
    return;
  }

  onError(response?.data.message);
};

const onExpireService = async (id: number) => {
  await treatmentPlansStore.expireTreatmentPlanService(id);
  await onSuccess('Service has been expired', 'Expired');
};

const onGoalRemoved = () => onSuccess('Goal has been removed', 'Saved');

const onNewGoal = () => onSuccess('Goal has been saved', 'Saved');

const onGoalUpdate = () => onSuccess('Goal has been updated', 'Saved');

const showExpired = () => {
  treatmentPlansStore.showExpired = true;
  getServices();
};

const hideExpired = () => {
  treatmentPlansStore.showExpired = false;
  getServices();
};

const rowClass = (data: TreatmentPlanServiceStudentInterface) => {
  return {
    'expired-row': data.is_expired,
  };
};
</script>

<template>
  <Card class="card-full-width">
    <template #header>
      <h2>Services</h2>
      <ExpireSwitch @enable="showExpired" @disable="hideExpired" />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.TREATMENT_PLANS_SERVICES_STORE])"
          id="add_service_btn"
          type="text"
          size="small"
          text="Add Service"
          icon-left="add"
          :loading="loadingServiceModal"
          @click="openServicesModal"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:expandedRows="expandedRows"
        :value="services"
        data-key="id"
        :row-hover="true"
        :lazy="true"
        :scrollable="true"
        loading-icon="logo-spinner"
        responsive-layout="scroll"
        :class="{ 'table-loading': loading }"
        :row-class="rowClass"
      >
        <template v-if="services.length">
          <Column
            :expander="can([PERMISSIONS.TREATMENT_SERVICES_GOALS_INDEX])"
            header-class="max-w-4rem"
            body-class="max-w-4rem"
          />
          <Column
            class="service service-table-item"
            field="service"
            header="Service"
            header-class="flex justify-content-start align-items-center"
            body-class="px-3 flex align-items-center"
            style="min-width: 270px"
          >
            <template #body="{ data }">
              <div class="flex flex-column gap-1" :class="data.is_expired ? 'expired' : ''">
                <div class="flex align-items-center">
                  <Icon
                    v-if="data.is_group"
                    name="group"
                    class="bg-blue text-color-blue-light border-round-xl mr-1"
                    title="Group"
                    style="padding: 2px; font-size: 0.75rem"
                  />
                  <span class="title w-full" v-text="data.service.name" />
                  <span v-if="data.is_expired" class="text-color-red">&nbsp;(Expired)</span>
                </div>
                <span
                  class="description px-2 py-1 surface-100 text-sm border-round-sm w-max"
                  v-text="data.discipline.name"
                />
              </div>
            </template>
          </Column>
          <Column
            field="duration_description"
            header="Duration"
            class="service-table-item"
            header-class="flex align-items-center"
          />
          <Column field="start_at" header="Start Date" class="service-table-item">
            <template #body="{ data }">
              <span v-text="useDateTime().getLocalDate(data.start_at)" />
            </template>
          </Column>
          <Column field="end_at" header="End Date" header-class="flex align-items-center" class="service-table-item">
            <template #body="{ data }">
              <span :class="{ 'text-color-red': !data.is_active }" v-text="useDateTime().getLocalDate(data.end_at)" />
            </template>
          </Column>
          <Column
            field="goals"
            header="Goals"
            header-class="flex align-items-center"
            class="service-table-item justify-content-center"
          >
            <template #body="{ data }">
              <p>{{ data.goals.length }}</p>
            </template>
          </Column>
          <Column
            header="Expiring"
            header-class="flex align-items-center"
            class="service-table-item justify-content-center"
          >
            <template #body="{ data }">
              <Icon v-if="data.expiring" name="check" class="text-lg text-alert-red" />
            </template>
          </Column>
          <Column
            field="actions"
            header="Actions"
            header-class="flex align-items-center"
            class="service-table-item justify-content-center"
          >
            <template #body="{ data }">
              <span
                v-if="can([PERMISSIONS.TREATMENT_PLANS_SERVICES_UPDATE])"
                v-tooltip.bottom="'Edit service'"
                class="px-2 py-1 cursor-pointer text-color-secondary m-icon edit"
                @click="openUpdateServicesModal(data)"
              ></span>
              <span
                v-if="can([PERMISSIONS.TREATMENT_PLANS_SERVICE_EXPIRE])"
                v-tooltip.bottom="'Expire service'"
                class="px-2 py-1 cursor-pointer text-color-secondary m-icon delete"
                @click="openExpireServiceModal(data)"
              ></span>
            </template>
          </Column>
        </template>
        <template v-if="can([PERMISSIONS.TREATMENT_SERVICES_GOALS_INDEX])" #expansion="{ data }">
          <div :id="`service-${data.id}-goals`" class="service-goals-container">
            <ServiceGoals
              :goals="data.goals"
              :service-id="data.id"
              :parent-element="`service-${data.id}-goals`"
              @remove="onGoalRemoved"
              @success="onSuccess"
              @update="onGoalUpdate"
              @new-goal="onNewGoal"
              @error="onError"
            ></ServiceGoals>
          </div>
        </template>
        <template #empty>No Services</template>
      </DataTable>
      <AddService v-if="serviceModalOpen" @save="onAddService" @cancel="onCancelAdd" />
      <UpdateService
        v-if="updateServiceModalOpen"
        :service="actualService"
        @update="onUpdateService"
        @cancel="onCancelUpdate"
      />
      <ConfirmAction
        v-if="toggleExpireService"
        header="Confirm service expire"
        accept-label="Yes"
        reject-label="No"
        @accept="onExpireService(actualService?.id)"
        @cancel="toggleExpireService = false"
      >
        <div>
          <div class="flex items-stretch">
            <span class="font-bold">
              <i class="pi pi-fill pi-exclamation-triangle" style="background;: 'rgb(var(--primary-500))'"></i>
            </span>
            <p class="ml-2">Expiring this service will also:</p>
          </div>

          <ol>
            <li>Remove the goals associated with the service</li>
            <li>
              Remove
              <span class="font-bold">{{ expireInformation }}</span>
              future events for this student
            </li>
          </ol>
        </div>
      </ConfirmAction>
      <ConfirmAction
        v-if="toggleNoOrganizationModal"
        header="Student needs to be connected to an Organization"
        :show-footer="false"
        accept-label="Ok"
        reject-label="Cancel"
        @cancel="toggleNoOrganizationModal = false"
      >
        <div class="flex text-gray-800">
          <Icon name="info" style="gray"></Icon>
          <span class="w-30rem pl-3">Please connect the student to an organization before creating services.</span>
        </div>
      </ConfirmAction>
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.p-datatable-table) {
  width: 100%;
  tbody,
  thead {
    tr {
      width: 100%;
      td {
        width: 100%;
      }
      th,
      td {
        .service-table-item {
          max-width: 12%;
        }

        .check {
          color: var(--primary-color);
        }

        .title {
          font-weight: 600;
        }

        .number {
          font-weight: 600;
          color: var(--primary-color);
        }

        .description {
          font-size: 10px;
          line-height: 12px;
        }

        &.service {
          text-align: left;
        }
      }
      th:first-of-type,
      td:first-of-type {
        justify-content: flex-start;
        text-align: left;
      }
      .expanded-row {
        max-width: 4%;
        min-width: 50px;
      }
      &.expired-row,
      &.expanded-row:hover {
        background-color: $grayLight !important;
      }
    }
  }
  .p-datatable-row-expansion td {
    max-width: calc(100% + 1rem);
    margin: -1rem -1rem 0rem -1rem;
    padding: 1rem 0rem 0rem 1rem;
    display: flex;
    flex-direction: column;
  }
}

.service-goals-container {
  width: 100%;
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  &::-webkit-scrollbar {
    width: 1rem;
  }
  &::-webkit-scrollbar-thumb {
    background: $grayMedium;
    border-radius: 0.5rem;
    border: 6px solid white;
  }
}

.text-alert-red {
  color: $errorColor;
}
</style>
